import { TYPE_LABEL, FAIL_LABEL, PROPOSAL_ORDER, PROPOSAL_STATUS } from "@/lib/sk";
import { readPref, writePref } from "@/lib/layout";
import { TYPE_OPTIONS, FAIL_OPTIONS, APPLICATION_OPTIONS, FEEDBACK_OPTIONS, BOOL_OPTIONS, FLANGE_OPTIONS, ACTION_OPTIONS, APPLICATION_LABEL, cellText } from "./model";

const P = (v) => v.proposal || {};
const sf = (v, k) => P(v).sf?.[k] ?? null;
const below = (v, k) => { const x = sf(v, k); return x != null && x < (P(v).requirement?.sf_min || 0); };

/**
 * Every column the valve sheet can show – one engine, several views. `kind` marks a composite, read-only cell (the work's
 * answer: valve, actuator, status, next step); `get` reads a derived value; everything else is a customer field a person
 * can edit. `w` is the default width, `min` the smallest a drag may make it.
 */
export const COLUMNS = [
  { key: "valve", label: "Tag / ventil", w: 210, min: 150, kind: "valve", ro: true, sticky: true, sort: (v) => v.tag },
  { key: "function", label: "Funksjon · feilposisjon", w: 170, min: 110, kind: "function", ro: true, sort: (v) => `${v.application || "~"} ${v.fail_mode || "~"}` },
  { key: "actuator", label: "Aktuator / konfigurasjon", w: 250, min: 170, kind: "actuator", ro: true, sort: (v) => P(v).code || "~" },
  { key: "tstatus", label: "Teknisk status", w: 250, min: 150, kind: "status", ro: true, sort: (v) => PROPOSAL_ORDER.indexOf(P(v).status) },
  { key: "next", label: "Neste steg", w: 200, min: 140, kind: "next", ro: true, sort: (v) => P(v).next_step?.label || "~" },
  { key: "tag", label: "Tag", w: 170, min: 110, ro: true, sticky: true, mono: true, sort: (v) => v.tag },
  { key: "proposal_code", label: "Typekode", w: 230, min: 140, kind: "code", ro: true, sort: (v) => P(v).code || "~" },
  { key: "proposal_status", label: "Status", w: 124, min: 90, kind: "pill", ro: true, sort: (v) => PROPOSAL_ORDER.indexOf(P(v).status) },
  { key: "why", label: "Årsak", w: 220, min: 140, ro: true, get: (v) => P(v).next_step?.why_label },
  { key: "type", label: "Type", w: 86, options: TYPE_OPTIONS, group: "Ventil" },
  { key: "dn", label: "DN", w: 60, numeric: true, group: "Ventil" },
  { key: "pn", label: "PN", w: 56, numeric: true, group: "Ventil" },
  { key: "valve_maker", label: "Produsent", w: 120, group: "Ventil" },
  { key: "valve_model", label: "Modell", w: 118, group: "Ventil" },
  { key: "valve_class", label: "Klasse", w: 90, group: "Ventil" },
  { key: "dp_bar", label: "ΔP bar", w: 66, numeric: true, group: "Moment" },
  { key: "bto_nm", label: "BTO", w: 72, numeric: true, group: "Moment" },
  { key: "etc_nm", label: "ETC", w: 72, numeric: true, group: "Moment" },
  { key: "mast_nm", label: "MAST", w: 76, numeric: true, group: "Moment" },
  { key: "torque_nm", label: "Moment", w: 76, numeric: true, group: "Moment" },
  { key: "torque_at_wp", label: "T ved WP", w: 84, numeric: true, ro: true, get: (v) => P(v).torque_at_wp, group: "Dimensjonering" },
  { key: "spring_end", label: "Fjær slutt", w: 84, numeric: true, ro: true, get: (v) => P(v).spring_end_nm, group: "Dimensjonering" },
  { key: "sf_min", label: "SF min", w: 64, numeric: true, ro: true, digits: 2, get: (v) => P(v).requirement?.sf_min, group: "Dimensjonering" },
  { key: "sf_bto", label: "SF BTO", w: 68, numeric: true, ro: true, digits: 2, get: (v) => sf(v, "bto"), bad: (v) => below(v, "bto"), group: "Dimensjonering" },
  { key: "sf_etc", label: "SF ETC", w: 68, numeric: true, ro: true, digits: 2, get: (v) => sf(v, "etc"), bad: (v) => below(v, "etc"), group: "Dimensjonering" },
  { key: "sf_spring", label: "SF fjær", w: 68, numeric: true, ro: true, digits: 2, get: (v) => sf(v, "spring"), bad: (v) => below(v, "spring"), group: "Dimensjonering" },
  { key: "mast_margin", label: "MAST-margin %", w: 108, numeric: true, ro: true, get: (v) => sf(v, "mast_margin_pct"), bad: (v) => (sf(v, "mast_margin_pct") ?? 0) < 0, group: "Dimensjonering" },
  { key: "checks", label: "Kontroller", w: 120, kind: "checks", ro: true, group: "Dimensjonering" },
  { key: "flange", label: "Flens", w: 66, options: FLANGE_OPTIONS, group: "Tilkobling" },
  { key: "std_flange", label: "Aktuatorflens", w: 104, ro: true, get: (v) => P(v).std_flange, group: "Tilkobling" },
  { key: "adapter", label: "Adapter", w: 130, kind: "adapter", ro: true, group: "Tilkobling" },
  { key: "stem", label: "Spindel", w: 80, group: "Tilkobling" },
  { key: "application", label: "Funksjon", w: 98, options: APPLICATION_OPTIONS, group: "Styring" },
  { key: "fail_mode", label: "Feilmodus", w: 94, options: FAIL_OPTIONS, group: "Styring" },
  { key: "action", label: "Virkemåte", w: 110, options: ACTION_OPTIONS, group: "Styring" },
  { key: "feedback", label: "Fjernind.", w: 112, options: FEEDBACK_OPTIONS, group: "Styring" },
  { key: "mech_indicator", label: "Mek.ind.", w: 72, options: BOOL_OPTIONS, group: "Styring" },
  { key: "closing_time_s", label: "Lukk s", w: 62, numeric: true, group: "Styring" },
  { key: "opening_time_s", label: "Åpne s", w: 62, numeric: true, group: "Styring" },
  { key: "esd_group", label: "ESD", w: 60, group: "Styring" },
  { key: "pid", label: "P&ID", w: 80, group: "Annet" },
  { key: "pipe_tag", label: "Rør", w: 90, group: "Annet" },
  { key: "system", label: "System", w: 110, group: "Annet" },
  { key: "zone", label: "Sone", w: 80, group: "Annet" },
  { key: "remarks", label: "Merknad", w: 180, group: "Annet" },
];
export const BY_KEY = Object.fromEntries(COLUMNS.map((c) => [c.key, c]));
export const EDITABLE = COLUMNS.filter((c) => !c.ro);

/** Four cuts of the same data. Arbeidsoversikt shows the decisions first; the others are the sheet for one task. */
export const VIEWS = [
  { key: "arbeid", label: "Arbeidsoversikt", density: "comfortable", cols: ["valve", "function", "actuator", "tstatus", "next"] },
  { key: "dim", label: "Dimensjonering", density: "compact", cols: ["tag", "type", "dn", "dp_bar", "bto_nm", "etc_nm", "mast_nm", "proposal_code", "torque_at_wp", "spring_end", "sf_min", "sf_bto", "sf_etc", "sf_spring", "mast_margin", "checks", "proposal_status"] },
  { key: "styring", label: "Styring og tilkobling", density: "compact", cols: ["tag", "type", "dn", "application", "fail_mode", "feedback", "mech_indicator", "closing_time_s", "opening_time_s", "esd_group", "flange", "std_flange", "adapter", "stem", "proposal_code", "proposal_status"] },
  { key: "alle", label: "Alle felt", density: "compact", cols: ["tag", "type", "dn", "pn", "valve_maker", "valve_model", "valve_class", "dp_bar", "bto_nm", "etc_nm", "mast_nm", "torque_nm", "flange", "stem", "application", "fail_mode", "action", "feedback", "mech_indicator", "closing_time_s", "opening_time_s", "esd_group", "proposal_code", "sf_bto", "sf_etc", "sf_spring", "proposal_status", "why", "pid", "pipe_tag", "system", "zone", "remarks"] },
];
export const viewOf = (key) => VIEWS.find((x) => x.key === key) || VIEWS[0];
export const DENSITY = { comfortable: { row: 44, text: "text-[13.5px]" }, compact: { row: 32, text: "text-[13px]" } };
/** On a phone the work view keeps the three answers that matter: the valve, the actuator, the next step. */
export const NARROW_COLS = { arbeid: ["valve", "actuator", "next"] };

/** What a cell says when it is copied: the words the sheet shows, never raw codes the next sheet cannot read. */
export function plainText(col, v) {
  const p = v.proposal || {};
  switch (col.kind) {
    case "valve": return v.tag || "";
    case "function": return [APPLICATION_LABEL[v.application], v.fail_mode ? FAIL_LABEL[v.fail_mode] : null].filter(Boolean).join(" · ");
    case "actuator": case "code": return p.code || "";
    case "status": case "pill": return PROPOSAL_STATUS[p.status]?.label || "";
    case "next": return p.next_step?.label || "";
    case "checks": return (p.checks || []).map((c) => `${c.label}: ${c.ok === true ? "ok" : c.ok === false ? "feil" : "?"}`).join(", ");
    case "adapter": return p.flange?.needed === false ? "direkte" : p.flange?.line?.name || (p.flange?.ok === false ? "uavklart" : "");
    default: {
      if (col.get) { const x = col.get(v); return x == null ? "" : typeof x === "number" ? String(x) : String(x); }
      return cellText(col.key, v[col.key]);
    }
  }
}

/* Per-user memory (same mechanism as the panel widths): which view, and per view the order, hidden columns, widths, density, sort, grouping. */
const PREF = (view) => `ventiler.view.${view}`;
export function readViewPrefs(user, view) { try { return JSON.parse(readPref(PREF(view), user) || "null") || {}; } catch { return {}; } }
export function writeViewPrefs(user, view, prefs) { const clean = Object.fromEntries(Object.entries(prefs || {}).filter(([, x]) => x != null && !(Array.isArray(x) && !x.length) && !(typeof x === "object" && !Array.isArray(x) && !Object.keys(x).length))); writePref(PREF(view), user, Object.keys(clean).length ? JSON.stringify(clean) : null); }
export const readActiveView = (user) => { const k = readPref("ventiler.active", user); return VIEWS.some((v) => v.key === k) ? k : "arbeid"; };
export const writeActiveView = (user, k) => writePref("ventiler.active", user, k);

/** The columns a view shows, in the person's order and widths. */
export function resolveColumns(viewKey, prefs) {
  const view = viewOf(viewKey);
  const hidden = new Set(prefs.hidden || []);
  const widths = prefs.widths || {};
  return (prefs.cols || view.cols).filter((k) => !hidden.has(k) && BY_KEY[k]).map((k) => ({ ...BY_KEY[k], w: widths[k] || BY_KEY[k].w }));
}
export const densityOf = (viewKey, prefs) => prefs.density || viewOf(viewKey).density;

export const sortValue = (col, v) => (col.sort ? col.sort(v) : col.get ? col.get(v) : v[col.key]);
export function sortRows(rows, sort) {
  const col = sort?.key ? BY_KEY[sort.key] : null;
  if (!col) return rows;
  const dir = sort.dir === "desc" ? -1 : 1;
  return [...rows].sort((a, b) => {
    const x = sortValue(col, a); const y = sortValue(col, b);
    if (x == null && y == null) return 0; if (x == null) return 1; if (y == null) return -1;
    if (typeof x === "number" && typeof y === "number") return (x - y) * dir;
    return String(x).localeCompare(String(y), "nb", { numeric: true }) * dir;
  });
}

export const GROUPS = [["none", "Ingen gruppering"], ["why", "Avklaringsårsak"], ["system", "System"], ["config", "Konfigurasjonstype"], ["type", "Ventiltype"], ["status", "Teknisk status"]];
/** Rows → blocks for one grouping. A block opens to the individual valves; it never replaces them or their approval. */
export function groupRows(rows, by) {
  if (!by || by === "none") return null;
  const keyOf = (v) => {
    const p = P(v);
    switch (by) {
      case "why": return [p.next_step?.why_key || "none", p.next_step?.why_label || "Uten årsak"];
      case "system": return [v.system || "", v.system || "Uten system"];
      case "config": return [p.code || "", p.code || "Ingen aktuator"];
      case "type": return [v.type || "", TYPE_LABEL[v.type] || "Ukjent type"];
      case "status": return [p.status || "", PROPOSAL_STATUS[p.status]?.label || "–"];
      default: return ["", ""];
    }
  };
  const map = new Map();
  rows.forEach((v) => { const [k, label] = keyOf(v); if (!map.has(k)) map.set(k, { key: k || "_", label, rows: [] }); map.get(k).rows.push(v); });
  const blocks = [...map.values()];
  if (by === "status") blocks.sort((a, b) => PROPOSAL_ORDER.indexOf(a.key) - PROPOSAL_ORDER.indexOf(b.key));
  else if (by === "system" || by === "type") blocks.sort((a, b) => a.label.localeCompare(b.label, "nb", { numeric: true }));
  else blocks.sort((a, b) => b.rows.length - a.rows.length);
  return blocks;
}
